import { forwardRef } from 'react';
import { type QuizQuoteApiPlanType, type QuizQuoteDisplayData } from '../../utils';
import styles from '../QuoteResultPageView.module.css';
const ASSET_BASE = '/assets/pricing/quote/document';
const PLAN_LABEL_KO = {
  basic: '베이직',
  pro: '프로'
} as const;
const PLAN_LABEL_EN = {
  basic: 'Basic',
  pro: 'Pro'
} as const;
interface QuizQuoteDocumentProps {
  quote: QuizQuoteDisplayData;
  submittedPlanType: QuizQuoteApiPlanType;
  today: string;
}
function formatWon(value: number): string {
  return value.toLocaleString('ko-KR');
}
function formatKoreanAmount(value: number): string {
  if (value === 0) return '영원';
  const digits = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
  const smallUnits = ['', '십', '백', '천'];
  const largeUnits = ['', '만', '억', '조'];
  const groups = Math.floor(value).toString().split('').reverse();
  const parts: string[] = [];
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 4) {
    const groupDigits = groups.slice(groupIndex, groupIndex + 4);
    const groupText = groupDigits.map((digit, digitIndex) => {
      const number = Number(digit);
      if (!number) return '';
      if (number === 1 && digitIndex > 0) return smallUnits[digitIndex];
      return `${digits[number]}${smallUnits[digitIndex]}`;
    }).reverse().join('');
    if (groupText) {
      parts.unshift(`${groupText}${largeUnits[groupIndex / 4]}`);
    }
  }
  return `${parts.join('')}원`;
}
function AmountCell({
  amount,
  compact = false,
  parenthesized = false
}: {
  amount: number;
  compact?: boolean;
  parenthesized?: boolean;
}) {
  const className = [styles.amountCell, compact ? styles.amountCellCompact : '', parenthesized ? styles.amountCellParenthesized : ''].filter(Boolean).join(' ');
  return <span className={className} data-sentry-component="AmountCell" data-sentry-source-file="QuizQuoteDocument.tsx">
      <span className={styles.currency}>₩</span>
      <span className={styles.amountValue}>{formatWon(amount)}</span>
    </span>;
}
function formatDocumentDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
}
function getPlanFeature(plan: QuizQuoteDisplayData['plan']): string {
  if (plan === 'basic') {
    return '베이직 전용 맵 이용, AI로 문제 추가하기, 비공개 퀴즈 만들기';
  }
  return '모든 맵 무제한 이용, 고급 AI 기능, 모든 학습리포트 기능, 다음 퀴즈 이어풀기, 워크시트 저장';
}
export const QuizQuoteDocument = forwardRef<HTMLDivElement, QuizQuoteDocumentProps>(function QuizQuoteDocument({
  quote,
  today
}, ref) {
  const annotation = quote.etc || '특이사항 없음';
  const emptyRowCount = quote.hasGroupDiscount ? 3 : 4;
  return <div ref={ref} className={styles.est}>
      <div className={styles.documentHeader}>
        ZEP QUIZ {PLAN_LABEL_KO[quote.plan]} 플랜 견적서
      </div>
      <div className={`${styles.logo} ${styles.headerLogo}`}>
        <img src={`${ASSET_BASE}/logo.svg`} alt="ZEP Logo" />
      </div>

      <table className={styles.infoTable}>
        <tbody>
          <tr>
            <th>수신</th>
            <td>{quote.schoolName}</td>
            <th rowSpan={4}>
              공
              <br />
              급
              <br />자
            </th>
            <th>등록번호</th>
            <td colSpan={3}>535-86-02323</td>
          </tr>
          <tr>
            <th>견적명</th>
            <td>
              ZEP QUIZ {PLAN_LABEL_KO[quote.plan]} 플랜 {quote.term}개월 견적
            </td>
            <th>상호명</th>
            <td>주식회사 젭</td>
            <th>대표자</th>
            <td>김상엽, 고창원</td>
          </tr>
          <tr>
            <th>견적날짜</th>
            <td>{today}</td>
            <th>주소</th>
            <td colSpan={3}>서울시 강남구 선릉로 93길 50, 11층</td>
          </tr>
          <tr>
            <th>유효기간</th>
            <td>견적일로부터 15일</td>
            <th>전화번호</th>
            <td>070-7666-9837</td>
            <th>이메일</th>
            <td>biz@zep.us</td>
          </tr>
        </tbody>
      </table>

      <table className={styles.infoTable}>
        <tbody>
          <tr>
            <th>견적가</th>
            <td className={styles.strong}>
              {`금 ${formatKoreanAmount(quote.finalAmount)} 정(₩${formatWon(quote.finalAmount)}), VAT 포함`}
            </td>
          </tr>
        </tbody>
      </table>

      <table className={styles.priceTable}>
        <thead>
          <tr>
            <th colSpan={6}>산출 내역</th>
          </tr>
        </thead>
        <tbody>
          <tr className={styles.strong}>
            <td>No.</td>
            <td>플랜</td>
            <td>사용 인원</td>
            <td>1인 당 단가</td>
            <td>이용 개월 수</td>
            <td>결제 금액</td>
          </tr>
          <tr>
            <td>1</td>
            <td>
              <strong>{PLAN_LABEL_EN[quote.plan]}</strong>
            </td>
            <td>{quote.userCount}</td>
            <td>
              <AmountCell amount={quote.regularAmount} compact />
            </td>
            <td>{quote.term}</td>
            <td>
              <AmountCell amount={quote.totalAmount} />
            </td>
          </tr>
          {quote.hasGroupDiscount ? <tr>
              <td>2</td>
              <td colSpan={4}>학교·단체 할인</td>
              <td>
                <AmountCell amount={quote.discountAmount} parenthesized />
              </td>
            </tr> : null}
          {Array.from({
          length: emptyRowCount
        }).map((_, index) => <tr key={index}>
              <td> &nbsp;</td>
              <td />
              <td />
              <td />
              <td />
              <td />
            </tr>)}
          <tr>
            <td colSpan={5} className={styles.finalPrice}>
              최종 견적
            </td>
            <td className={styles.finalPrice}>
              <AmountCell amount={quote.finalAmount} />
            </td>
          </tr>
        </tbody>
      </table>

      <table className={styles.detailsTable}>
        <thead>
          <tr>
            <th colSpan={3}>기능 상세</th>
          </tr>
          <tr>
            <th>번 호</th>
            <th>항 목</th>
            <th>내 용</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>사용 인원</td>
            <td>{quote.userCount}명</td>
          </tr>
          <tr>
            <td>2</td>
            <td>사용 시작일</td>
            <td>{formatDocumentDate(quote.startDate)}</td>
          </tr>
          <tr>
            <td>3</td>
            <td>만료일</td>
            <td>{formatDocumentDate(quote.endDate)}</td>
          </tr>
          <tr>
            <td>4</td>
            <td>{PLAN_LABEL_KO[quote.plan]} 플랜 기능</td>
            <td>{getPlanFeature(quote.plan)}</td>
          </tr>
          <tr>
            <td>5</td>
            <td>요금제 유형</td>
            <td>{quote.hasGroupDiscount ? '학교·단체' : '개인'}</td>
          </tr>
        </tbody>
      </table>

      <table className={styles.detailsTable}>
        <thead>
          <tr>
            <th colSpan={2}>비고</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={styles.annotation}>{annotation}</td>
            <td className={styles.sign}>
              &nbsp;
              <br />
              &nbsp;
              <img src={`${ASSET_BASE}/zep_stamp.png`} alt="" />
              <br />
              &nbsp;
            </td>
          </tr>
        </tbody>
      </table>
    </div>;
});